import React, { useState } from 'react';
import { FaBookmark } from 'react-icons/fa';
import { useTranslation } from '../localization/useAppTranslation';
import type { SavedPromptFromChatRequest } from '../types/preferences';
import { ThemedSpinner } from './ThemedSpinner';
import '../styles/MessageActionButton.css';
import '../styles/SavePromptFromResponseButton.css';

interface SavePromptFromResponseButtonProps {
  sourcePrompt: SavedPromptFromChatRequest;
  onGenerateDraft: (sourcePrompt: SavedPromptFromChatRequest) => Promise<void>;
  disabled?: boolean;
}

export const SavePromptFromResponseButton: React.FC<SavePromptFromResponseButtonProps> = ({
  sourcePrompt,
  onGenerateDraft,
  disabled = false,
}) => {
  const { t } = useTranslation('preferences');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const handleGenerateDraft = async () => {
    if (isGenerating || disabled) {
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    try {
      await onGenerateDraft(sourcePrompt);
    } catch {
      setGenerationError(t('savedPrompts.fromResponse.error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const actionLabel = t('savedPrompts.fromResponse.action');
  const isActionDisabled = isGenerating || disabled;
  const statusLabel = isActionDisabled ? t('savedPrompts.fromResponse.preparing') : actionLabel;

  return (
    <div className="save-prompt-from-response-action">
      <button
        type="button"
        className="message-action-button save-prompt-from-response-button"
        onClick={() => void handleGenerateDraft()}
        disabled={isActionDisabled}
        aria-label={statusLabel}
        title={actionLabel}
      >
        {isGenerating ? <ThemedSpinner size="sm" ariaLabel={statusLabel} /> : (
          <FaBookmark aria-hidden="true" />
        )}
      </button>
      {generationError && (
        <span className="save-prompt-from-response-error" role="alert">
          {generationError}
        </span>
      )}
    </div>
  );
};